import React, { useEffect, useState, Suspense, lazy, createElement, Component } from 'react';
import { Check, X, ShieldCheck, Star, Target, Smartphone, Palette, Flame, Calendar, Search, Trophy, Edit3, ChevronDown, ChevronLeft, ChevronRight, Quote, Clock, Zap, Users, ClipboardList, Calculator, Sparkles, Package, UtensilsCrossed, AlertTriangle, MessageSquare } from 'lucide-react';
import { CTAButton } from './components/CTAButton';
// CommentsSection is at the end of the page — lazy-loaded so it doesn't bloat
// the initial JS bundle. Reduces FCP/TBT on mobile.
const CommentsSection = lazy(() => import('./components/CommentsSection').then(m => ({
  default: m.CommentsSection
})));
// SocialProofPopup is below-the-fold and only appears after 4s — load it lazily
// to keep it out of the critical bundle and improve mobile FCP/LCP.
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/qEyeyNbxYSKdmd69ryJYda/mocap_gastronomico-sin_fondo.webp';
// Mobile/tablet variants are served via the wsrv.nl image proxy (free, 99.9% uptime,
// production-grade). The proxy fetches the original 1080×1080 webp and resizes it on
// the fly. Visually identical on small screens, but ~5–10× smaller in bytes → huge LCP win.
const _heroEncoded = encodeURIComponent(HERO_IMAGE);
const HERO_IMAGE_MOBILE = `https://wsrv.nl/?url=${_heroEncoded}&w=640&output=webp&q=60`;
const HERO_IMAGE_TABLET = `https://wsrv.nl/?url=${_heroEncoded}&w=900&output=webp&q=80`;
const CHECKOUT_URL = 'https://italia.recursopack.com/cart/40987513684017:1';
// Synchronous head injection — runs at module parse time, BEFORE React renders.
// Order matters: hero preload FIRST so the browser's preload scanner discovers it
// before anything else (including CSS), then preconnects, then async font loading.
if (typeof document !== 'undefined') {
  const head = document.head;
  const firstChild = head.firstChild;
  // 1. HERO IMAGE PRELOAD — top priority for LCP (must come before CSS link)
  const heroPreload = document.createElement('link');
  heroPreload.rel = 'preload';
  heroPreload.as = 'image';
  heroPreload.href = HERO_IMAGE;
  heroPreload.type = 'image/webp';
  (heroPreload as any).fetchPriority = 'high';
  (heroPreload as any).imageSrcset = `${HERO_IMAGE_MOBILE} 640w, ${HERO_IMAGE_TABLET} 900w, ${HERO_IMAGE} 1080w`;
  (heroPreload as any).imageSizes = '(max-width: 640px) 320px, (max-width: 768px) 384px, 512px';
  head.insertBefore(heroPreload, firstChild);
  // 2. PRECONNECTS — set up TCP/TLS early
  const addPreconnect = (href: string, anonymous = false) => {
    const l = document.createElement('link');
    l.rel = 'preconnect';
    l.href = href;
    if (anonymous) l.crossOrigin = 'anonymous';
    head.insertBefore(l, firstChild);
  };
  // Only the most critical origins — too many preconnects hurts performance.
  addPreconnect('https://wsrv.nl', true);
  addPreconnect('https://cdn.magicpatterns.com', true);
  addPreconnect('https://fonts.gstatic.com', true);
  // 3. META PIXEL — standard Meta snippet, synchronous at module parse time.
  const w = window as any;
  if (!w.fbq) {
    const n: any = w.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    head.appendChild(t);
    n('init', '1671983810480785');
    n('track', 'PageView');
  }
}
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const previewSlides = [{
    title: 'Confronto Margini di Contribuzione',
    subtitle: 'Tomo 1 — Costi e Ingegneria del Menu',
    image: 'https://cdn.magicpatterns.com/uploads/4oyQhQejLbXkA4KNV5fHZx/Tomo-6-IL-PROTOCOLLO-LONGEVITA.pdf_(4).webp'
  }, {
    title: 'Checklist Cambio Turno',
    subtitle: 'Tomo 2 — Operazioni Quotidiane',
    image: 'https://cdn.magicpatterns.com/uploads/1nXAZAYqWmkK7zeSutUnWj/Tomo-6-IL-PROTOCOLLO-LONGEVITA.pdf_(3).webp'
  }, {
    title: 'Protocollo in Caso di Ispezione',
    subtitle: 'Tomo 3 — Igiene e Sicurezza',
    image: 'https://cdn.magicpatterns.com/uploads/gAGJoB7w8hpAkdpKi2HBZj/Tomo-6-IL-PROTOCOLLO-LONGEVITA.pdf_(2).webp'
  }, {
    title: 'Le 7 Tecniche di Upselling',
    subtitle: 'Tomo 4 — Servizio e Vendite',
    image: 'https://cdn.magicpatterns.com/uploads/u6s79xCs23VEtHpLsZjnKi/Tomo-6-IL-PROTOCOLLO-LONGEVITA.pdf_(1).webp'
  }, {
    title: 'Il Sistema FIFO Applicato',
    subtitle: 'Tomo 5 — Controllo dello Stock',
    image: 'https://cdn.magicpatterns.com/uploads/docskC7RuHNetvikueWh52/Tomo-6-IL-PROTOCOLLO-LONGEVITA.pdf.webp'
  }, {
    title: 'Piano Formazione Annuale',
    subtitle: 'Tomo 6 — Gestione del Team',
    image: 'https://cdn.magicpatterns.com/uploads/6NqjU3HBfa7bEv2rB4TtRu/Tomo-6-IL-PROTOCOLLO-LONGEVITA.pdf_(5).webp'
  }];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % previewSlides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + previewSlides.length) % previewSlides.length);
  const faqs = [{
    q: 'È davvero tutto in PDF?',
    a: 'Sì. Nessun software. Nessun abbonamento. Nessuna piattaforma da imparare. Scarichi i file e li usi subito — su qualsiasi dispositivo.'
  }, {
    q: 'Funziona anche per il mio tipo di locale?',
    a: 'Sì. Il sistema è stato progettato per ristoranti, pizzerie, trattorie, bar, pasticcerie, take away e qualsiasi locale del settore gastronomico in Italia.'
  }, {
    q: 'Devo avere competenze particolari?',
    a: 'No. Ogni guida è scritta in italiano chiaro e diretto. Se gestisci un locale, capisci tutto immediatamente.'
  }, {
    q: 'Quando ricevo i materiali?',
    a: "Immediatamente dopo il pagamento. Ricevi un'email con tutti i link per scaricare i tuoi PDF."
  }, {
    q: 'E se non sono soddisfatto?',
    a: 'Hai 7 giorni di garanzia totale. Ti rimborsiamo senza fare domande.'
  }, {
    q: 'Perché costa così poco?',
    a: 'Perché è un prodotto digitale. Non ci sono costi di stampa, spedizione o intermediari. Questo risparmio lo passiamo direttamente a te.'
  }];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const testimonials = [{
    name: 'Marco T.',
    role: 'Titolare trattoria, Bologna',
    text: 'Finalmente qualcosa di pratico e concreto. Ho trovato 3 perdite nascoste nel mio locale già nella prima settimana.'
  }, {
    name: 'Claudia R.',
    role: 'Titolare pizzeria, Napoli',
    text: 'Ho ridotto gli sprechi del 30% in un mese. Non credevo fosse possibile con una guida PDF.'
  }, {
    name: 'Antonio M.',
    role: 'Titolare ristorante, Milano',
    text: 'Finalmente mi sono liberato dalla dipendenza totale dal mio cuoco. Il sistema funziona.'
  }];
  const finalTestimonials = [{
    name: 'Stefano B.',
    role: 'Titolare ristorante, Roma',
    text: 'Ho implementato il sistema di cambio turno e in due settimane ho azzerato i problemi di comunicazione tra i miei camerieri.'
  }, {
    name: 'Giovanna F.',
    role: 'Titolare trattoria, Firenze',
    text: 'La guida sui costi mi ha aperto gli occhi. Stavo vendendo tre piatti in perdita da due anni senza saperlo.'
  }, {
    name: 'Roberto C.',
    role: 'Titolare pizzeria, Torino',
    text: "Finalmente ho superato un'ispezione ASL senza stress. Tutto documentato e in ordine."
  }];
  return <div className="min-h-screen bg-dark-900 text-gray-300 selection:bg-gold-500 selection:text-black pb-20 overflow-x-hidden" data-id="element-0">
      {/* 1. Attention Bar */}
      <div className="w-full bg-dark-800 border-b border-white/5 py-3 px-4 flex justify-center items-center z-50" data-id="element-1">
        <div className="flex items-center gap-3 max-w-4xl mx-auto text-center" data-id="element-2">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse-fast shrink-0 shadow-[0_0_10px_rgba(239,68,68,0.8)]" data-id="element-3"></div>
          <p className="text-xs md:text-sm font-semibold tracking-wider text-gray-300 uppercase" data-id="element-4">
            OFFERTA LANCIO LIMITATA — SOLO PER I PRIMI 200 TITOLARI · PREZZO
            BLOCCATO A €19,90
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-20" data-id="element-5">
        {/* 2. Hero Section */}
        <div className="text-center space-y-5 md:space-y-8 mb-8 md:mb-12" data-id="element-6">
          <h1 className="text-3xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight" data-id="element-7">
            Il tuo locale lavora tanto ma{' '}
            <span className="gold-gradient-text" data-id="element-8">i soldi non tornano mai?</span>
          </h1>

          <div className="max-w-3xl mx-auto space-y-4 md:space-y-6" data-id="element-9">
            <div className="relative pl-5 md:pl-6 py-2 text-left" data-id="element-10">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-gold-400 to-gold-600 rounded-full" data-id="element-11"></div>
              <p className="text-base md:text-xl text-gray-300 leading-relaxed font-medium" data-id="element-12">
                Il sistema completo usato dai{' '}
                <span className="text-white font-bold" data-id="element-13">
                  ristoratori professionisti
                </span>{' '}
                per tagliare i costi, eliminare il caos e guadagnare davvero —
                senza consulenti, senza software costosi e senza perdere altro
                tempo.
              </p>
            </div>
            <p className="text-lg md:text-2xl font-bold text-gold-400" data-id="element-14">
              Sistema di Gestione Gastronomica Professionale
            </p>
          </div>
        </div>

        {/* 3. Product Image & 4. Social Proof */}
        <div className="flex flex-col items-center mb-16" data-id="element-15">
          <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-lg mx-auto mb-8" data-id="element-16">
            <div className="absolute inset-0 bg-gold-500/10 blur-[100px] rounded-full pointer-events-none blur-glow" data-id="element-17"></div>
            <img src={HERO_IMAGE} srcSet={`${HERO_IMAGE_MOBILE} 640w, ${HERO_IMAGE_TABLET} 900w, ${HERO_IMAGE} 1080w`} sizes="(max-width: 640px) 320px, (max-width: 768px) 384px, 512px" alt="Sistema di Gestione Gastronomica Professionale Bundle" width={1080} height={1080} fetchPriority="high" decoding="async" className="relative z-10 w-full h-auto drop-shadow-2xl md:hover:scale-[1.02] transition-transform duration-500" data-id="element-18" />
          </div>

          <div className="flex flex-col items-center space-y-3 mb-8" data-id="element-19">
            <div className="flex items-center gap-1 text-gold-400" data-id="element-20">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-21" />)}
            </div>
            <p className="text-white font-semibold text-lg" data-id="element-22">
              4.9/5 (+1.200 Recensioni Verificate)
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 font-medium text-sm text-center" data-id="element-23">
              <Flame className="w-4 h-4 shrink-0" data-id="element-24" />
              67 titolari hanno scelto questo Sistema oggi
            </div>
          </div>

          {/* 5. First CTA Button */}
          <CTAButton subtext="⚡ Accesso Immediato • 🛡️ Garanzia 7 Giorni • 💳 Pagamento Unico" scrollTo="offerta" data-id="element-25">
            🍽️ SÌ! VOGLIO IL SISTEMA COMPLETO
          </CTAButton>

          {/* 6. European flags trust strip */}
          <div className="mt-6 flex flex-col items-center gap-3 text-center" data-id="element-26">
            <div className="flex gap-2 text-2xl" data-id="element-27">🇮🇹 🇨🇭 🇪🇸 🇫🇷 🇩🇪</div>
            <p className="text-sm text-gray-400 max-w-md" data-id="element-28">
              La risorsa N.1 scelta da migliaia di titolari di locali in tutta
              Europa.
            </p>
          </div>
        </div>

        {/* 7. The Problem (Persuasion paragraph) */}
        <div className="mb-20 cv-auto" data-id="element-29">
          <div className="glass-panel p-8 md:p-10 rounded-2xl border-gold-500/20 relative overflow-hidden" data-id="element-30">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-gold-400 to-gold-600" data-id="element-31"></div>
            <Quote className="absolute top-6 right-6 w-24 h-24 text-white/5 rotate-180 pointer-events-none" data-id="element-32" />
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed relative z-10 font-medium" data-id="element-33">
              <span className="text-white font-bold" data-id="element-34">
                Gestire un locale in Italia oggi è diventato quasi impossibile.
              </span>{' '}
              I costi salgono ogni mese, il personale cambia continuamente, i
              fornitori fanno quello che vogliono. Lavori 12 ore al giorno e
              alla fine del mese ti chiedi{' '}
              <span className="text-gold-400 font-bold" data-id="element-35">
                dove sono finiti i soldi
              </span>
              . Non è colpa tua — nessuno ti ha mai dato un sistema concreto per
              gestire tutto in modo professionale. Fino ad oggi.
            </p>
          </div>
        </div>

        {/* 7b. Initial Social Proof Testimonials (replaces carousel) */}
        <div className="mb-20 cv-auto" data-id="element-36">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-37">
            Cosa dicono i titolari
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-38">
            {testimonials.map((t, i) => <div key={i} className="glass-panel p-6 rounded-2xl border-white/5 relative" data-id="element-39">
                <div className="flex items-center gap-1 text-gold-400 mb-3" data-id="element-40">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-41" />)}
                </div>
                <p className="text-gray-300 leading-relaxed text-sm mb-5 italic" data-id="element-42">
                  "{t.text}"
                </p>
                <div className="border-t border-white/5 pt-4" data-id="element-43">
                  <p className="text-white font-bold text-sm" data-id="element-44">{t.name}</p>
                  <p className="text-gray-500 text-xs" data-id="element-45">{t.role}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 8. Comparison Table */}
        <div className="mb-20 cv-auto" data-id="element-46">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-47">
            La tua trasformazione
          </h2>
          <div className="glass-panel rounded-2xl overflow-hidden" data-id="element-48">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-49">
              <div className="p-6 md:p-8 bg-dark-800/50 border-b md:border-b-0 md:border-r border-white/5" data-id="element-50">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2" data-id="element-51">
                  SENZA IL SISTEMA
                  <span className="text-sm font-normal text-gray-500 block mt-1" data-id="element-52">
                    (Cosa ti costa oggi)
                  </span>
                </h3>
                <ul className="space-y-6" data-id="element-53">
                  {['Costi che salgono ogni mese senza controllo.', 'Personale che cambia di continuo e turnover alto.', 'Sprechi di magazzino e fornitori che dettano legge.', 'Stress per ispezioni ASL senza protocolli pronti.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-54">
                      <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-55">
                        <X className="w-4 h-4 text-red-500" data-id="element-56" />
                      </div>
                      <span className="leading-relaxed" data-id="element-57">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="p-6 md:p-8 bg-emerald-900/10 relative overflow-hidden" data-id="element-58">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[80px] rounded-full pointer-events-none blur-glow" data-id="element-59"></div>
                <h3 className="text-xl font-bold text-emerald-400 mb-6 flex items-center gap-2 relative z-10" data-id="element-60">
                  CON IL SISTEMA
                  <span className="text-sm font-normal text-emerald-500/70 block mt-1" data-id="element-61">
                    (Il tuo locale domani)
                  </span>
                </h3>
                <ul className="space-y-6 relative z-10" data-id="element-62">
                  {['Food cost reale di ogni piatto e menu che genera profitto.', 'Staff formato, motivato e meno rotazione.', 'Magazzino sotto controllo e zero sprechi nascosti.', 'Ispezioni superate con HACCP pronto e documentato.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-white font-medium" data-id="element-63">
                      <div className="mt-1 bg-emerald-500/20 p-1 rounded-full shrink-0" data-id="element-64">
                        <Check className="w-4 h-4 text-emerald-400" data-id="element-65" />
                      </div>
                      <span className="leading-relaxed" data-id="element-66">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 9. Questa risorsa è per te se... */}
        <div className="mb-20 cv-auto" data-id="element-67">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-68">
            Questo sistema è per te se...
          </h2>
          <div className="glass-panel p-8 rounded-2xl border-white/10" data-id="element-69">
            <ul className="space-y-5" data-id="element-70">
              {['Hai un ristorante, pizzeria, trattoria, bar o locale di qualsiasi tipo in Italia.', 'Sei stanco di lavorare tanto e guadagnare poco a fine mese.', 'Vuoi smettere di essere indispensabile per ogni piccola cosa.', 'Vuoi un locale che funzioni anche quando non ci sei tu.', 'Vuoi proteggere il tuo locale dalle ispezioni sanitarie.', 'Vuoi ridurre i costi senza abbassare la qualità.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-71">
                  <div className="mt-1 bg-gold-500/20 p-1.5 rounded-full shrink-0" data-id="element-72">
                    <Check className="w-5 h-5 text-gold-400" data-id="element-73" />
                  </div>
                  <span className="text-lg text-gray-300 leading-relaxed" data-id="element-74">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* 10. Cosa otterrai con questo Sistema (Mecanismo unico) */}
        <div className="mb-20 cv-auto" data-id="element-75">
          <h2 className="text-3xl font-bold text-center text-white mb-4" data-id="element-76">
            Perché questo sistema è diverso
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-77">
            Niente teoria, niente abbonamenti, niente consulenti. Strumenti
            pronti da usare.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-78">
            {[{
            icon: Zap,
            title: 'Operativo, non teorico',
            desc: 'Ogni pagina è uno strumento pronto da usare oggi nel tuo locale, non un manuale accademico.',
            color: 'text-amber-400',
            bg: 'bg-amber-400/10'
          }, {
            icon: Trophy,
            title: 'Immediato',
            desc: 'Nessuna formazione, nessun software da imparare. Apri il PDF e applichi subito.',
            color: 'text-emerald-400',
            bg: 'bg-emerald-400/10'
          }, {
            icon: Smartphone,
            title: 'Completo',
            desc: 'Copre ogni area del locale: costi, operazioni, igiene, vendite, magazzino e team.',
            color: 'text-blue-400',
            bg: 'bg-blue-400/10'
          }, {
            icon: Target,
            title: 'È tuo per sempre',
            desc: 'Pagamento unico. Nessun abbonamento. Lo usi quando vuoi, come vuoi, su ogni dispositivo.',
            color: 'text-purple-400',
            bg: 'bg-purple-400/10'
          }].map((item, i) => <div key={i} className="glass-panel p-6 rounded-2xl border-white/5 hover:border-white/10 transition-colors" data-id="element-79">
                <div className={`w-12 h-12 rounded-xl ${item.bg} flex items-center justify-center mb-4`} data-id="element-80">
                  <item.icon className={`w-6 h-6 ${item.color}`} data-id="element-81" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-82">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-83">{item.desc}</p>
              </div>)}
          </div>
        </div>

        {/* 11. I 6 Tomi del Sistema */}
        <div className="mb-20 cv-auto" data-id="element-84">
          <div className="text-center mb-12" data-id="element-85">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-86">
              I 6 Tomi del{' '}
              <span className="gold-gradient-text" data-id="element-87">Sistema Completo</span>
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-88">
              Strumenti digitali in PDF pronti da stampare o consultare sul tuo
              tablet da oggi stesso.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-89">
            {[{
            icon: Calculator,
            num: '1',
            title: 'Costi e Ingegneria del Menu',
            desc: 'Scopri il food cost reale di ogni piatto e prezza il menu per guadagnare davvero. Trova le perdite nascoste in pochi giorni.'
          }, {
            icon: ClipboardList,
            num: '2',
            title: 'Operazioni Quotidiane',
            desc: 'Checklist di apertura, turno e chiusura per non dimenticare mai nulla. Il tuo locale gira anche senza di te.'
          }, {
            icon: ShieldCheck,
            num: '3',
            title: 'Igiene e Sicurezza',
            desc: 'Protocolli HACCP pronti per superare qualsiasi ispezione ASL senza stress né ansia.'
          }, {
            icon: UtensilsCrossed,
            num: '4',
            title: 'Servizio e Vendite',
            desc: 'Forma il tuo staff per vendere di più e servire meglio ogni giorno. Aumenta lo scontrino medio.'
          }, {
            icon: Package,
            num: '5',
            title: 'Controllo dello Stock',
            desc: 'Gestisci magazzino e fornitori senza sprechi e senza sorprese. Recupera margine ogni mese.'
          }, {
            icon: Users,
            num: '6',
            title: 'Gestione del Team',
            desc: 'Riduci il turnover e costruisci uno staff professionale e motivato. Smetti di dipendere dal cuoco.'
          }].map((item, i) => <div key={i} className="glass-panel p-8 rounded-2xl relative overflow-hidden group" data-id="element-90">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/5 rounded-full blur-3xl group-hover:bg-gold-500/10 transition-colors blur-glow" data-id="element-91"></div>
                <div className="flex items-center gap-4 mb-5 relative z-10" data-id="element-92">
                  <div className="w-14 h-14 rounded-xl bg-dark-800 border border-white/10 flex items-center justify-center shrink-0" data-id="element-93">
                    <item.icon className="w-7 h-7 text-gold-400" data-id="element-94" />
                  </div>
                  <span className="text-xs font-bold text-gold-400 uppercase tracking-widest bg-gold-500/10 px-3 py-1 rounded-full border border-gold-500/20" data-id="element-95">
                    Tomo {item.num}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-96">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed relative z-10 text-sm" data-id="element-97">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* 12b. Preview Carousel — Sfoglia il materiale */}
        <div className="mb-24 cv-auto" data-id="element-98">
          <div className="text-center mb-10" data-id="element-99">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-100">
              Sfoglia il materiale all'interno
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-101">
              Un'anteprima reale delle pagine che troverai nei tomi: schede
              operative, checklist e tabelle pronte all'uso.
            </p>
            <p className="text-gold-400 font-medium mt-4" data-id="element-102">
              {currentSlide + 1} / {previewSlides.length}
            </p>
          </div>

          <div className="relative max-w-2xl mx-auto" data-id="element-103">
            {/* Navigation Buttons */}
            <button onClick={prevSlide} aria-label="Pagina precedente" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 transition-colors z-20 shadow-xl" data-id="element-104">
              <ChevronLeft className="w-6 h-6" data-id="element-105" />
            </button>

            <button onClick={nextSlide} aria-label="Pagina successiva" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 transition-colors z-20 shadow-xl" data-id="element-106">
              <ChevronRight className="w-6 h-6" data-id="element-107" />
            </button>

            {/* Carousel Track */}
            <div className="overflow-hidden rounded-2xl glass-panel border-gold-500/20 relative bg-dark-800" data-id="element-108">
              <div className="flex transition-transform duration-500 ease-out" style={{
              transform: `translateX(-${currentSlide * 100}%)`
            }} data-id="element-109">
                {previewSlides.map((slide, i) => <div key={i} className="min-w-full relative" data-id="element-110">
                    <img src={slide.image} alt={`${slide.subtitle} — ${slide.title}`} loading="lazy" decoding="async" width={1080} height={1527} className="w-full h-auto object-contain bg-white" data-id="element-111" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6 pt-20" data-id="element-112">
                      <p className="text-xs font-bold text-gold-400 uppercase tracking-widest mb-1" data-id="element-113">
                        {slide.subtitle}
                      </p>
                      <h3 className="text-lg md:text-xl font-bold text-white leading-tight" data-id="element-114">
                        {slide.title}
                      </h3>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-6" data-id="element-115">
              {previewSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} aria-label={`Vai alla pagina ${i + 1}`} className={`h-2 rounded-full transition-all ${i === currentSlide ? 'w-8 bg-gold-400' : 'w-2 bg-white/20 hover:bg-white/40'}`} data-id="element-116" />)}
            </div>
          </div>
        </div>

        {/* 13. Bonuses Section */}
        <div className="mb-24 relative cv-auto" data-id="element-117">
          <div className="absolute inset-0 bg-gold-500/5 blur-[120px] rounded-full pointer-events-none blur-glow" data-id="element-118"></div>
          <div className="glass-panel border-gold-500/20 p-8 md:p-12 rounded-3xl relative z-10" data-id="element-119">
            <div className="text-center mb-12" data-id="element-120">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-121">
                6 Bonus Esclusivi Inclusi
              </h2>
              <p className="text-gold-400 font-medium text-lg uppercase tracking-wider" data-id="element-122">
                (Senza costi extra)
              </p>
              <div className="inline-block mt-4 px-6 py-2 rounded-full bg-dark-800 border border-white/10" data-id="element-123">
                <p className="text-gray-300 font-semibold" data-id="element-124">
                  Valore totale dei bonus:{' '}
                  <span className="text-white" data-id="element-125">€ 120</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-126">
              {[{
              icon: Zap,
              title: 'Kit di Avvio 72 Ore',
              desc: 'Le prime azioni concrete da fare nelle prossime 72 ore per vedere risultati immediati nel tuo locale.'
            }, {
              icon: ClipboardList,
              title: 'Checklist Maestra del Titolare',
              desc: 'La lista definitiva di tutto ciò che il titolare deve controllare ogni giorno, settimana e mese.'
            }, {
              icon: MessageSquare,
              title: 'Modelli Express di Comunicazione',
              desc: 'Template pronti per parlare con il team in modo chiaro, autorevole e senza fraintendimenti.'
            }, {
              icon: AlertTriangle,
              title: 'Kit Anti-Caos Cambio Turno',
              desc: 'Procedure precise per il passaggio di consegne tra turni: zero errori, zero malumori.'
            }, {
              icon: Search,
              title: 'Dove Stai Perdendo Soldi Oggi?',
              desc: 'Guida diagnostica per identificare in 30 minuti tutte le falle nascoste che ti stanno svuotando le casse.'
            }, {
              icon: Calendar,
              title: 'Planner Operativo Settimanale',
              desc: 'Il planner stampabile per pianificare ogni settimana del tuo locale come un manager professionista.'
            }].map((bonus, i) => <div key={i} className="flex flex-col sm:flex-row items-start gap-5 p-6 rounded-2xl bg-dark-800/80 border border-white/5 hover:border-gold-500/30 transition-colors" data-id="element-127">
                  <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center shrink-0 border border-gold-500/20" data-id="element-128">
                    <bonus.icon className="w-6 h-6 text-gold-400" data-id="element-129" />
                  </div>
                  <div className="flex-1" data-id="element-130">
                    <div className="flex items-center justify-between gap-2 mb-2" data-id="element-131">
                      <h4 className="text-white font-bold text-lg leading-tight" data-id="element-132">
                        {bonus.title}
                      </h4>
                      <span className="px-2.5 py-1 text-[10px] font-black bg-emerald-500/20 text-emerald-400 rounded uppercase tracking-wider shrink-0" data-id="element-133">
                        Gratis
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-134">
                      {bonus.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* 14. Pricing Section */}
        <div id="offerta" className="mb-16 text-center scroll-mt-8" data-id="element-135">
          <h2 className="text-3xl font-bold text-white mb-10" data-id="element-136">
            L'Offerta "Sistema Completo"
          </h2>

          <div className="inline-block w-full max-w-2xl glass-panel p-8 md:p-12 rounded-3xl border-gold-500/40 relative overflow-hidden text-left shadow-[0_0_50px_rgba(251,191,36,0.05)]" data-id="element-137">
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-12 py-2 rotate-45 translate-x-10 translate-y-6 shadow-lg z-20 tracking-wider" data-id="element-138">
              93% OFF
            </div>

            <div className="flex justify-center mb-8" data-id="element-139">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-500/20 border border-gold-500/30 text-gold-400 text-sm font-bold tracking-wide uppercase" data-id="element-140">
                <Star className="w-4 h-4 fill-current" data-id="element-141" />
                LA SCELTA PIÙ POPOLARE
              </div>
            </div>

            <p className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-6 border-b border-white/10 pb-4" data-id="element-142">
              VALORE TOTALE DEI CONTENUTI:
            </p>

            <ul className="space-y-4 mb-8" data-id="element-143">
              {[{
              name: 'Tomo 1 — Costi e Ingegneria del Menu',
              price: '€ 27'
            }, {
              name: 'Tomo 2 — Operazioni Quotidiane',
              price: '€ 27'
            }, {
              name: 'Tomo 3 — Igiene e Sicurezza',
              price: '€ 27'
            }, {
              name: 'Tomo 4 — Servizio e Vendite',
              price: '€ 27'
            }, {
              name: 'Tomo 5 — Controllo dello Stock',
              price: '€ 27'
            }, {
              name: 'Tomo 6 — Gestione del Team',
              price: '€ 27'
            }, {
              name: '6 Bonus Esclusivi',
              price: '€ 120'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-gray-300" data-id="element-144">
                  <span className="flex items-center gap-2" data-id="element-145">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-146" />
                    {item.name}
                  </span>
                  <span className="font-medium shrink-0" data-id="element-147">{item.price}</span>
                </li>)}
            </ul>

            <div className="border-t border-white/10 pt-6 mb-8 flex justify-between items-center" data-id="element-148">
              <span className="text-gray-400 font-medium" data-id="element-149">VALORE REALE:</span>
              <span className="text-xl text-gray-500 line-through decoration-red-500/50 decoration-2 font-bold" data-id="element-150">
                € 282,00
              </span>
            </div>

            <div className="text-center space-y-2 mb-8 bg-dark-900/50 p-6 rounded-2xl border border-white/5" data-id="element-151">
              <p className="text-gold-400 font-bold tracking-widest uppercase text-sm" data-id="element-152">
                SOLO OGGI
              </p>
              <div className="flex items-start justify-center gap-1" data-id="element-153">
                <span className="text-3xl font-bold text-white mt-2" data-id="element-154">€</span>
                <span className="text-7xl font-black text-white tracking-tighter" data-id="element-155">
                  19
                </span>
                <span className="text-3xl font-bold text-white mt-2" data-id="element-156">,90</span>
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 text-sm text-gray-400 font-medium mb-8 text-center" data-id="element-157">
              <p data-id="element-158">💳 Pagamento unico • ⚡ Accesso immediato</p>
              <p className="text-red-400 flex items-center gap-1" data-id="element-159">
                <Clock className="w-4 h-4" data-id="element-160" /> Solo per i primi 200 titolari —
                poi torna a € 47
              </p>
            </div>

            {/* 15. Second CTA */}
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" onClick={() => {
            const fbq = (window as any).fbq;
            if (typeof fbq === 'function') {
              fbq('track', 'InitiateCheckout', {
                value: 19.9,
                currency: 'EUR',
                content_name: 'Sistema di Gestione Gastronomica'
              });
            }
          }} className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)] no-underline" data-id="element-161">
              <span className="flex items-center gap-2 text-xl tracking-wide uppercase text-shadow-sm" data-id="element-162">
                🍽️ ACQUISTA ORA — € 19,90
              </span>
            </a>
            <p className="mt-4 text-sm text-center text-gray-400 font-medium" data-id="element-163">
              ⚡ Accesso Immediato • 💳 Un solo Pagamento • 🛡️ Garanzia di 7
              Giorni
            </p>
          </div>
        </div>

        {/* 16. Guarantee Section */}
        <div className="max-w-3xl mx-auto glass-panel p-8 md:p-10 rounded-3xl flex flex-col md:flex-row items-center gap-8 text-center md:text-left border-emerald-500/30 mb-20 relative overflow-hidden cv-auto" data-id="element-164">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none blur-glow" data-id="element-165"></div>
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 border border-emerald-500/20 relative z-10" data-id="element-166">
            <ShieldCheck className="w-10 h-10 text-emerald-400" data-id="element-167" />
          </div>
          <div className="relative z-10" data-id="element-168">
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-169">
              GARANZIA TOTALE 7 GIORNI O RIMBORSO
            </h3>
            <p className="text-gray-300 text-base leading-relaxed" data-id="element-170">
              Scarica tutto. Leggi tutto. Usalo nel tuo locale. Se entro 7
              giorni non sei soddisfatto al 100% ti restituiamo ogni centesimo.
              Nessuna domanda. Nessuna burocrazia. Il rischio è completamente
              nostro.
            </p>
          </div>
        </div>

        {/* 17. About / Quote */}
        <div className="max-w-3xl mx-auto mb-20 text-center cv-auto" data-id="element-171">
          <div className="relative w-48 h-48 mx-auto mb-8" data-id="element-172">
            <div className="absolute inset-0 bg-gold-500/10 blur-[60px] rounded-full pointer-events-none blur-glow" data-id="element-173"></div>
            <img src={HERO_IMAGE} alt="Sistema di Gestione Gastronomica" loading="lazy" decoding="async" width={384} height={384} className="relative z-10 w-full h-full object-contain drop-shadow-2xl" data-id="element-174" />
          </div>
          <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-widest" data-id="element-175">
            La nostra missione
          </h3>
          <p className="text-xl md:text-2xl text-gray-400 italic leading-relaxed font-serif" data-id="element-176">
            "Abbiamo creato questo sistema per i titolari italiani che lavorano
            duro ma non vedono i risultati. Niente teoria, niente consulenti
            costosi. Solo strumenti pratici per gestire il tuo locale come un
            vero professionista."
          </p>
        </div>

        {/* 17b. Community Comments — lazy loaded */}
        <Suspense fallback={<div className="mb-20 min-h-[400px]" data-id="element-178" />} data-id="element-177">
          <CommentsSection data-id="element-179" />
        </Suspense>

        {/* 18. FAQ Section */}
        <div className="max-w-3xl mx-auto mb-20 cv-auto" data-id="element-180">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-181">
            Domande Frequenti
          </h2>
          <div className="space-y-4" data-id="element-182">
            {faqs.map((faq, index) => <div key={index} className="glass-panel rounded-xl border-white/5 overflow-hidden transition-all duration-300" data-id="element-183">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none hover:bg-white/[0.02] transition-colors" data-id="element-184">
                  <span className="text-lg font-semibold text-white pr-4" data-id="element-185">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gold-400 shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-186" />
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-60 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-187">
                  <p className="text-gray-400 leading-relaxed border-t border-white/5 pt-4" data-id="element-188">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 19. Final CTA */}
        <div className="mb-10" data-id="element-189">
          <CTAButton subtext="⚡ Accesso Immediato • 🛡️ Garanzia di 7 Giorni" scrollTo="offerta" data-id="element-190">
            🍽️ SÌ! VOGLIO RIPRENDERE IL CONTROLLO DEL MIO LOCALE
          </CTAButton>
        </div>
      </main>

      {/* 20. Footer */}
      <footer className="mt-12 border-t border-white/5 pt-12 pb-8 px-4 text-center cv-auto" data-id="element-191">
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-192">
          <div className="flex items-center justify-center gap-2 text-gray-400 font-bold text-lg" data-id="element-193">
            <UtensilsCrossed className="w-5 h-5" data-id="element-194" /> Sistema di Gestione
            Gastronomica
          </div>
          <p className="text-gray-500 text-sm leading-relaxed" data-id="element-195">
            Il metodo definitivo per gestire il tuo locale senza improvvisare.
            Strumenti pratici per titolari che vogliono risultati concreti.
          </p>
          <p className="text-gray-600 text-xs pt-4" data-id="element-196">
            © 2026 — Tutti i diritti riservati. Prodotto digitale — PDF
            scaricabile immediatamente.
          </p>
        </div>
      </footer>

      <Suspense fallback={null} data-id="element-197">
        <SocialProofPopup data-id="element-198" />
      </Suspense>
    </div>;
}